/**
 * ForecastYtdBox — the FY2026 year-to-date position (D-19), at the FOOT of the
 * tab, mirroring the CEO artifact v29 `.ytd` block (quick-260918-fai).
 *
 * WHAT IT SHOWS: GROSS ONLY. One column per FY2026 quarter Q1..Q4 — on every
 * period, including CQ, because the page merges the NQ/FY payload quarters in —
 * with the rows Gross sales / Gross budget / vs budget / %. Q1 and Q2 are
 * banked actuals; Q3 and Q4 carry the live gross call at the viewer's Upside %.
 * Every column reads gross against the quarter's GROSS ARR budget with a
 * signed, coloured variance. There is no churn and no net anywhere in the box
 * (John, 2026-09-18 — the v25–v28 net model is superseded). The total is Q1 +
 * Q2 + Q3 gross, captioned 'At end of Q3', against the Q1+Q2+Q3 gross budgets;
 * Q4 is a column, never part of the total. The box is the last thing on the
 * tab rather than the first — D-19 puts it below Managed Services.
 *
 * TWO INVARIANTS THIS FILE IS RESPONSIBLE FOR:
 *
 *  1. THE TOTAL REFORECASTS. The live ARR call arrives as an ARGUMENT
 *     (`arrByQuarterLabel`), never as a server field, and the arithmetic is
 *     re-run on EVERY render. There is deliberately no caching here — a value
 *     baked at load would leave this box disagreeing with the equation strip
 *     directly above it the moment a viewer moved the Upside %.
 *
 *  2. A BANKED QUARTER IS RENDERED, NOT DERIVED. Per D-20 the FY2026 Q1/Q2
 *     gross figures are manual finance-workbook values seeded by migration
 *     058; they are NOT derivable from Salesforce (a closed-won aggregate
 *     produces a materially lower number). They arrive already resolved on the
 *     payload and this component renders exactly what it is given. It has no
 *     fallback source, and it computes no banked gross of its own. A banked
 *     quarter that arrives with a null growth is a SEEDING GAP for the
 *     migration and the reader to answer: it renders UNRESOLVED, because a
 *     plausible substitute figure is the one outcome D-20 exists to prevent.
 *
 * Per D-06 the reading is BOTH a percentage and a raw signed over/under; per
 * D-13d there is no explanatory sentence — the title is the whole header.
 */
import { grossYtd } from '../../lib/forecast-weighting.js';
import { formatAmount, pctOf, signed } from '../../lib/format.js';
import { Card } from '../ui/Card.js';

import type { ForecastYtdQuarter } from '@saledash/shared';

/**
 * Placeholder for a figure the model did not resolve — a banked quarter's
 * unseeded gross (D-20), or an open quarter with no live call yet.
 */
const UNRESOLVED = '—';

/** The artifact's `.ytd-row.dim` — an unresolved row and its value both dim. */
const DIM_CLASS = 'text-ink-400 font-medium';

/** D-06 colour for a signed reading: green at or above zero, red below. */
function toneClass(value: number): string {
  return value < 0 ? 'text-bad-text' : 'text-good-text';
}

export interface ForecastYtdBoxProps {
  /**
   * The FY2026 quarters to show, one column each, in any order. The page
   * supplies the union of the CQ, NQ and FY payload quarters
   * (`mergeYtdQuarters`) so Q4 is present on every period (quick-260917-nfy).
   */
  quarters: readonly ForecastYtdQuarter[];
  /**
   * The LIVE weighted ARR call per quarter label, at the viewer's current
   * Upside %. Supplied by the page from the same weighting module the equation
   * strip reads, so the two cannot disagree. A banked quarter ignores it.
   */
  arrByQuarterLabel: ReadonlyMap<string, number>;
  /**
   * quick-260921-f73 — the fiscal quarter the total is read AT THE END OF: the
   * quarter on screen (3 on the current-quarter view, 4 on next-quarter and
   * full-year). Omitted ⇒ the model's default (first unbanked quarter).
   */
  asOfQuarter?: number | undefined;
}

interface YtdLineProps {
  label: string;
  testId: string;
  /** The formatted value, or null to render the UNRESOLVED dash, dimmed. */
  value: string | null;
  /** Extra classes on the value span for a resolved value (size, tone). */
  valueClass?: string | undefined;
  /** Extra classes on the row (the budget row's dashed rule). */
  rowClass?: string | undefined;
}

/** One label/value line of a quarter column. */
function YtdLine({ label, testId, value, valueClass, rowClass }: YtdLineProps): React.JSX.Element {
  const dim = value === null;
  return (
    <div
      className={`flex items-baseline justify-between gap-2 whitespace-nowrap text-[0.74rem] ${
        dim ? DIM_CLASS : 'text-ink-500'
      } ${rowClass ?? ''}`}
    >
      <span>{label}</span>
      <span
        className={`tabular-nums ${dim ? DIM_CLASS : `font-semibold ${valueClass ?? 'text-ink-900'}`}`}
        data-testid={testId}
      >
        {value ?? UNRESOLVED}
      </span>
    </div>
  );
}

export function ForecastYtdBox({
  quarters,
  arrByQuarterLabel,
  asOfQuarter,
}: ForecastYtdBoxProps): React.JSX.Element {
  // Recomputed every render BY DESIGN (D-19) — see invariant 1 in the header.
  const result = grossYtd(quarters, arrByQuarterLabel, asOfQuarter);

  const totalCaption =
    result.asOfQuarter === null ? 'Year to date' : `At end of Q${String(result.asOfQuarter)}`;
  const totalPct = pctOf(result.grossTotal, result.grossBudget);

  return (
    <Card data-testid="forecast-ytd-box" aria-label="FY2026 year to date against budget">
      <p className="text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
        FY2026 year to date
      </p>

      {/* quick-260918-fai: six equal columns at lg+ (Q1..Q4, total, verdict)
          so "vs YTD budget" sits to the RIGHT of "At end of Q3" on one row,
          never wrapped under Q1; auto-fit below lg. Rows are nowrap so a
          seven-digit gross never pushes one column's rows out of line. Every
          value is the same size; gross is bold. */}
      <div className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-x-4 gap-y-3 lg:grid-cols-6">
        {result.rows.map((row) => {
          // The artifact prints the percent with an explicit '+' at or above
          // budget; pctOf already carries the U+2212 on negatives, so the sign
          // is never doubled.
          const pctText =
            row.vsBudget === null
              ? null
              : (() => {
                  const p = pctOf(row.vsBudget, row.grossBudget);
                  return p === null ? null : `${row.vsBudget >= 0 ? '+' : ''}${p}`;
                })();
          return (
            <div
              key={row.label}
              data-testid={`forecast-ytd-row-${row.label}`}
              className="flex flex-col gap-0.5 border-l border-ink-200 pl-3 first:border-l-0 first:pl-0"
            >
              <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-ink-500">
                Q{String(row.fiscalQuarter)} {row.banked ? 'actual' : 'forecast'}
              </p>
              <YtdLine
                label="Gross sales"
                testId={`forecast-ytd-gross-${row.label}`}
                value={row.gross === null ? null : formatAmount(row.gross)}
                valueClass="font-bold text-ink-900"
              />
              <YtdLine
                label="Gross budget"
                testId={`forecast-ytd-budget-${row.label}`}
                value={formatAmount(row.grossBudget)}
                rowClass="mt-2 border-t border-dashed border-ink-200 pt-1.5"
              />
              <YtdLine
                label="vs budget"
                testId={`forecast-ytd-vsbudget-${row.label}`}
                value={row.vsBudget === null ? null : signed(row.vsBudget)}
                valueClass={row.vsBudget === null ? undefined : toneClass(row.vsBudget)}
              />
              <YtdLine
                label=""
                testId={`forecast-ytd-pct-${row.label}`}
                value={pctText}
                valueClass={row.vsBudget === null ? undefined : toneClass(row.vsBudget)}
              />
            </div>
          );
        })}

        <div className="flex flex-col gap-0.5 border-l border-ink-300 pl-3" data-testid="forecast-ytd-total">
          <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-ink-500">
            {totalCaption}
          </p>
          <p
            className="whitespace-nowrap text-lg font-bold tabular-nums text-ink-900"
            data-testid="forecast-ytd-gross-total"
          >
            {formatAmount(result.grossTotal)}
          </p>
          <p className="text-[0.72rem] tabular-nums text-ink-500" data-testid="forecast-ytd-gross-budget">
            against {formatAmount(result.grossBudget)} YTD budget
          </p>
        </div>

        <div
          className="flex flex-col gap-0.5 border-l border-ink-200 pl-3"
          data-testid="forecast-ytd-verdict"
        >
          <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-ink-500">
            vs YTD budget
          </p>
          {/* D-06 — the raw signed over/under AND the percentage. */}
          <p
            className={`whitespace-nowrap text-lg font-bold tabular-nums ${toneClass(result.delta)}`}
            data-testid="forecast-ytd-delta"
          >
            {signed(result.delta)}
          </p>
          <p className="text-[0.72rem] tabular-nums text-ink-500" data-testid="forecast-ytd-pct">
            {totalPct ?? UNRESOLVED} of YTD budget
          </p>
        </div>
      </div>
    </Card>
  );
}
